import { forwardRef, useState } from 'react';

import { RowWrapper } from 'modules/workflowNodeEditors/EditorBody.styled';
import { ChevronDown } from 'ui/icons';
import { BasicSelect } from 'ui/selects';
import { Label } from 'ui/theme';

import { DropdownContent } from './DropdownContent';
import {
  SELECTIVE_TARGETING_OPTIONS,
  SELECTIVE_TARGETING_PLACEHOLDER_OPTIONS,
  SelectiveTargetingOptionValues,
} from './SelectiveTargeting.constants';
import {
  AppsCounter,
  AppsListPreview,
  AppsListSubtextContainer,
  DropdownButton,
  SelectiveTargetingSection,
  StyledDropdownContentContainer,
  TriggerPreview,
} from './SelectiveTargeting.styled';
import {
  SelectiveTargetingBaseProps,
  SelectiveTargetingDropdownTriggerProps,
  SelectiveTargetingOption,
} from './SelectiveTargeting.types';
import { combineSubtextVisibleListWithOverflow, getAppsListSubtext } from './utils';

const targetingOptions = SELECTIVE_TARGETING_OPTIONS.filter(
  (option) =>
    option.value === SelectiveTargetingOptionValues.ALL_MOBILE_APPS ||
    option.value === SelectiveTargetingOptionValues.CUSTOM,
);
const getTargetingOptionFromValue = (value: SelectiveTargetingOptionValues) =>
  targetingOptions.find((option) => option.value === value) || targetingOptions[0];

export const SelectivePush = ({
  mobileApps = [],
  mobileAppIds,
  setMobileAppIds,
  setIsSelectiveTargetingEnabled,
}: Omit<SelectiveTargetingBaseProps, 'isSelectiveTargetingEnabled'>) => {
  const [selectedTargetingOption, setSelectedTargetingOption] = useState(
    getTargetingOptionFromValue(
      mobileAppIds.length === 0
        ? SelectiveTargetingOptionValues.ALL_MOBILE_APPS
        : SelectiveTargetingOptionValues.CUSTOM,
    ),
  );

  const onChangeSelectedTargetingOption = (option: SelectiveTargetingOption) => {
    setIsSelectiveTargetingEnabled(option.value === SelectiveTargetingOptionValues.CUSTOM);
    setMobileAppIds([]);
    setSelectedTargetingOption(option);
  };

  const getDropdownContent = () => (
    <StyledDropdownContentContainer>
      <DropdownContent
        selectedTargetingOption={selectedTargetingOption}
        onChangeSelectedTargetingOption={onChangeSelectedTargetingOption}
        targetingOptions={targetingOptions}
        mobileApps={mobileApps}
        mobileAppIds={mobileAppIds}
        setMobileAppIds={setMobileAppIds}
      />
    </StyledDropdownContentContainer>
  );

  const Trigger = forwardRef<HTMLButtonElement, SelectiveTargetingDropdownTriggerProps>(
    ({ toggleDropdown }, ref) => (
      <DropdownButton ref={ref} onClick={toggleDropdown}>
        <TriggerPreview>
          {selectedTargetingOption.name}
          <AppsCounter>
            {selectedTargetingOption.value === SelectiveTargetingOptionValues.CUSTOM
              ? mobileAppIds.length
              : mobileApps.length}
          </AppsCounter>
        </TriggerPreview>
        <ChevronDown height={16} width={16} />
      </DropdownButton>
    ),
  );

  return (
    <RowWrapper>
      <SelectiveTargetingSection>
        <Label>Destination</Label>
        <BasicSelect
          id="push-destination-select"
          Option={getDropdownContent}
          options={SELECTIVE_TARGETING_PLACEHOLDER_OPTIONS}
          Trigger={Trigger}
          isFullWidth
        />
        <AppsListPreview>
          <AppsListSubtextContainer>
            {selectedTargetingOption.value === SelectiveTargetingOptionValues.CUSTOM
              ? combineSubtextVisibleListWithOverflow(
                  mobileApps.filter((app) => mobileAppIds.find((id) => app.id === id)),
                )
              : getAppsListSubtext(selectedTargetingOption, mobileApps)}
          </AppsListSubtextContainer>
        </AppsListPreview>
      </SelectiveTargetingSection>
    </RowWrapper>
  );
};
